import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CustomTable, ITableHeaderCell } from '../../../components/CustomTable/table';
import { TextWithOpenIcon } from '../../../components/TextWithOpenIcon';
import { getCurrencyInfoSelector } from '../../../ducks/transactions/selector';
import { clearCurrencyInfoThunk, getCurrencyInfoThunk } from '../../../ducks/transactions/thunk';
import { isCommonError } from '../../../helpers/typeGuards';
import { useAppParams } from '../../../hooks/useAppParams';
import { useIsOpen } from '../../../hooks/useIsOpen';
import { ThunkAppDispatch } from '../../../store';
import { ICurrency } from '../../../types/currency';
import { CurrencyRow } from './CurrencyRow';

const headersList: ITableHeaderCell[] = [
  {
    name: 'Initial Supply',
  },
  {
    name: 'Current Supply',
  },
  {
    name: 'Creation Height',
  },
  {
    name: 'Description',
  },
  {
    name: 'Issued by',
  },
  {
    name: 'Code',
  },
];

export const CurrencyInfoBlock = () => {
  const dispatch = useDispatch<ThunkAppDispatch>();
  const data = useSelector(getCurrencyInfoSelector);
  const history = useHistory();
  const params = useAppParams();
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, handleToggle, setClose, setOpen } = useIsOpen();

  const getData = useCallback(() => {
    if (!params?.code) {
      return dispatch(clearCurrencyInfoThunk());
    }
    setIsLoading(true);
    dispatch(clearCurrencyInfoThunk());
    dispatch(getCurrencyInfoThunk(params.code))
      .catch((err: unknown) => {
        if (isCommonError(err) && err.cause.errorCode === 5) {
          history.push('/transactions');
        }
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, params.code]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (params.transaction) {
      setClose();
    }

    if (!params.transaction && params.code) {
      setOpen();
    }
  }, [params.transaction, params.code, setOpen, setClose]);

  if (!params.code) return null;

  return (
    <div className='margin-bottom'>
      <TextWithOpenIcon
        text={`${data?.code ?? ''} Currency Information`}
        onToggle={handleToggle}
        isOpen={isOpen}
        isTitle
      />
      {isOpen && (
        <CustomTable<ICurrency>
          tableHeaders={headersList}
          data={data ? [data] : []}
          TableRow={CurrencyRow}
          emptyMessage='Currency not found.'
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
